/**
 * Palette DNA：从 HEX 列表推导影调文案与可注入渲染的偏置（启发式，不依赖网络）。
 */

export type PaletteDna = {
  summaryZh: string;
  /** 蓝/青家族的量化构成（含占比与代表 HEX），用于 UI/Prompt 更可解释 */
  blueCompositionZh: string;
  /** 加到每个光源 colorTemp（-100~100） */
  colorTempBias: number;
  /** 加到 saturation（0~100） */
  saturationDelta: number;
  /** 影调对比倾向 → 微调 intensity（约等于对比感） */
  intensityDelta: number;
};

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = hex.trim().match(/^#?([0-9a-f]{6})$/i);
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/** 相对亮度 0~1 */
function luminance(r: number, g: number, b: number): number {
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

type Hsv = { h: number; s: number; v: number };

function rgbToHsv(r: number, g: number, b: number): Hsv {
  const rf = r / 255;
  const gf = g / 255;
  const bf = b / 255;
  const max = Math.max(rf, gf, bf);
  const min = Math.min(rf, gf, bf);
  const d = max - min;

  let h = 0;
  if (d === 0) h = 0;
  else if (max === rf) h = ((gf - bf) / d) % 6;
  else if (max === gf) h = (bf - rf) / d + 2;
  else h = (rf - gf) / d + 4;
  h = Math.round(h * 60);
  if (h < 0) h += 360;

  const s = max === 0 ? 0 : d / max;
  const v = max;
  return { h, s, v };
}

type BlueBucket = 'cyan' | 'blue' | 'indigo';

function classifyBlue(r: number, g: number, b: number): BlueBucket | null {
  // 基于直觉：蓝色通常 b 通道更占优势，且饱和度不应太低
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const sat = max === 0 ? 0 : (max - min) / max;
  if (sat < 0.18) return null;

  const { h } = rgbToHsv(r, g, b);

  // cyan: 175-205, blue: 205-245, indigo: 245-290
  if (h >= 175 && h < 205) return 'cyan';
  if (h >= 205 && h < 245) return 'blue';
  if (h >= 245 && h < 290) return 'indigo';
  return null;
}

/**
 * 基于 5 个代表色的统计，生成中文摘要 + 偏置建议
 */
export function analyzePaletteHeuristic(hexColors: string[]): PaletteDna {
  const rgbs = hexColors.map(hexToRgb).filter(Boolean) as { r: number; g: number; b: number }[];
  if (rgbs.length === 0) {
    return {
      summaryZh: '暂无有效色样。',
      blueCompositionZh: '蓝/青家族：0/0（0%）。',
      colorTempBias: 0,
      saturationDelta: 0,
      intensityDelta: 0,
    };
  }

  const lum = rgbs.map((c) => luminance(c.r, c.g, c.b));
  const avgL = lum.reduce((a, b) => a + b, 0) / lum.length;
  const spread = Math.max(...lum) - Math.min(...lum);

  const avgR = rgbs.reduce((s, c) => s + c.r, 0) / rgbs.length;
  const avgB = rgbs.reduce((s, c) => s + c.b, 0) / rgbs.length;
  const warmth = (avgR - avgB) / 255;

  const parts: string[] = [];
  if (spread > 0.35) parts.push('对比偏强');
  else if (spread < 0.15) parts.push('对比柔和');
  if (warmth > 0.08) parts.push('整体偏暖');
  else if (warmth < -0.06) parts.push('整体偏冷');
  else parts.push('冷暖较中性');
  if (avgL < 0.35) parts.push('暗部较多');
  else if (avgL > 0.65) parts.push('整体偏亮');
  else parts.push('明暗适中');

  const summaryZh = `影调特征：${parts.join('，')}。`;

  // 蓝色量化构成：在“5个代表色”里统计蓝/青/靛的占比，并列出对应 HEX。
  const total = rgbs.length;
  const cyan: string[] = [];
  const blue: string[] = [];
  const indigo: string[] = [];
  for (let i = 0; i < hexColors.length; i++) {
    const rgb = hexToRgb(hexColors[i]);
    if (!rgb) continue;
    const bucket = classifyBlue(rgb.r, rgb.g, rgb.b);
    if (!bucket) continue;
    if (bucket === 'cyan') cyan.push(hexColors[i]);
    if (bucket === 'blue') blue.push(hexColors[i]);
    if (bucket === 'indigo') indigo.push(hexColors[i]);
  }

  const blueCount = cyan.length + blue.length + indigo.length;
  const pct = (n: number) => (total === 0 ? 0 : Math.round((n / total) * 100));

  let blueCompositionZh: string;
  if (blueCount === 0) {
    blueCompositionZh = `蓝/青家族：0/${total}（0%），本次代表色未捕捉到明显蓝色；渲染时可能缺少蓝色灯光倾向。`;
  } else {
    const cyanPart = cyan.length ? `青色 ${cyan.length}/${total}（${pct(cyan.length)}%）[${cyan.join(', ')}]` : '';
    const bluePart = blue.length ? `蓝色 ${blue.length}/${total}（${pct(blue.length)}%）[${blue.join(', ')}]` : '';
    const indigoPart = indigo.length ? `靛色/紫靛 ${indigo.length}/${total}（${pct(indigo.length)}%）[${indigo.join(', ')}]` : '';
    const partsZh = [cyanPart, bluePart, indigoPart].filter(Boolean).join('；');
    blueCompositionZh = `蓝/青家族：${blueCount}/${total}（${pct(blueCount)}%），其中${partsZh}。`;
  }

  const colorTempBias = Math.round(Math.max(-35, Math.min(35, warmth * 80)));
  const saturationDelta = Math.round(
    Math.max(-15, Math.min(20, (0.25 - spread) * 40)),
  );
  const intensityDelta =
    spread > 0.35 ? 0.08 : spread < 0.14 ? -0.05 : 0;

  return { summaryZh, blueCompositionZh, colorTempBias, saturationDelta, intensityDelta };
}
